import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SystemRole } from '@peles/shared';
import { HomePage } from '../pages/HomePage';
import AssistantPage from '../pages/AssistantPage';
import { LoginPage } from '../pages/LoginPage';
import { GoogleCallbackPage } from '../pages/GoogleCallbackPage';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import { UsersPage } from '../pages/UsersPage';
import { EventsPage } from '../pages/EventsPage';
import { EventWorkspacePage } from '../pages/EventWorkspacePage';
import { EventOverviewTab } from '../components/events/workspace/EventOverviewTab';
import {
  InvitesTab,
  InviteCampaignTab,
  SeatingTab,
  VendorsExpensesTab,
  MoneyTab,
  FilesPhotosTab,
  ConversationsTab,
  NotificationsTab,
  NotesTab,
} from '../components/events/workspace/EventTabPanels';
import { VenueDetailPage } from '../pages/VenueDetailPage';
import { VendorDetailPage } from '../pages/VendorDetailPage';
import { TemplatesPage } from '../pages/TemplatesPage';
import { TimelineTemplateEditPage } from '../pages/TimelineTemplateEditPage';
import { TimelineEditPage } from '../pages/TimelineEditPage';
import { VenuesPage } from '../pages/VenuesPage';
import { VendorsPage } from '../pages/VendorsPage';
import { ExpensesPage } from '../pages/ExpensesPage';
import { TimelinePage } from '../pages/TimelinePage';
import { TaskListPage } from '../pages/TaskListPage';
import { KibuddimPage } from '../pages/KibuddimPage';
import { GuestListPage } from '../pages/GuestListPage';
import { FloorPlanPage } from '../pages/FloorPlanPage';
import { PhotosPage } from '../pages/PhotosPage';
import { ProfilePage } from '../pages/ProfilePage';
import { AdminSqlClientPage } from '../pages/AdminSqlClientPage';
import { LogsPage } from '../pages/LogsPage';
import { LogAlertsPage } from '../pages/LogAlertsPage';
import { SettingsPage } from '../pages/SettingsPage';
import { ClientDashboardPage } from '../pages/ClientDashboardPage';
import { ClientEventDetailPage } from '../pages/ClientEventDetailPage';
import { VendorDashboardPage } from '../pages/VendorDashboardPage';
import { InvitationsPage } from '../pages/InvitationsPage';
import { ReportsPage } from '../pages/ReportsPage';
import { MailLogPage } from '../pages/MailLogPage';
import { AuditLogsPage } from '../pages/AuditLogsPage';
import { BackupsPage } from '../pages/BackupsPage';
import { DataImportPage } from '../pages/DataImportPage';
import { CalendarPage } from '../pages/CalendarPage';
import { InquiriesPage } from '../pages/InquiriesPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { CommunicationsPage } from '../pages/CommunicationsPage';
import { CommunicationHubPage } from '../pages/CommunicationHubPage';
import { SystemPage } from '../pages/SystemPage';
import { QuestionnairesPage } from '../pages/QuestionnairesPage';
import { QuestionnaireDetailPage } from '../pages/QuestionnaireDetailPage';
import { PublicQuestionnairePage } from '../pages/PublicQuestionnairePage';
import { PublicRsvpPage } from '../pages/PublicRsvpPage';
import { SeatFinderPage } from '../pages/SeatFinderPage';
import { PublicUploadPage } from '../pages/PublicUploadPage';
import { CheckInPage } from '../pages/CheckInPage';
import { CommissionsPage } from '../pages/CommissionsPage';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { AppLayout } from '../components/layout';
import { isPublicSite } from '../utils/is-public-site';
import { InviteShareRedirect } from '../components/invites/InviteShareRedirect';

/**
 * The Files page, split out of the main bundle.
 *
 * ⚠️ **Lazy because the file browser is heavy.** The page renders
 * `@speles7172/file-console`; bundled with the app it pushed `index.js` past
 * 2 MiB, the PWA's precache ceiling, and the build failed outright rather
 * than shipping something that would silently stop being cached.
 *
 * A route is the natural split: nobody loading the dashboard needs the file
 * browser, and this is the only page that carries it.
 */
const FilesPage = lazy(() =>
  import('../pages/FilesPage').then((module) => ({ default: module.FilesPage })),
);

export { isPublicSite };

/**
 * The public marketing site is its own chunk. It shares nothing with the app
 * beyond React and the router, and keeping it out of the main bundle keeps
 * that bundle under the service worker's precache limit — the app never pays
 * for the marketing pages and the marketing pages never pay for the app.
 */
const PublicLayout = lazy(() =>
  import('../components/layout/PublicLayout').then((m) => ({ default: m.PublicLayout })),
);
const LandingPage = lazy(() =>
  import('../pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const AboutPage = lazy(() => import('../pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const GalleryPage = lazy(() =>
  import('../pages/GalleryPage').then((m) => ({ default: m.GalleryPage })),
);

/** Ivory ground while a public chunk loads, so the page never flashes white. */
function PublicFallback() {
  return <div className="min-h-screen bg-brand-ground" aria-busy="true" />;
}

export function AppRouter() {
  if (isPublicSite()) {
    return (
      <Routes>
        <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
        <Route path="/rsvp/:token" element={<PublicRsvpPage />} />
        <Route path="/rsvp/:token/seat" element={<SeatFinderPage />} />
        <Route path="/i/:token" element={<InviteShareRedirect />} />
        <Route path="/upload/:token" element={<PublicUploadPage />} />
        {/* The address older emails carry — the same page. */}
        <Route path="/vendor-upload/:token" element={<PublicUploadPage />} />
        <Route
          element={
            <Suspense fallback={<PublicFallback />}>
              <PublicLayout />
            </Suspense>
          }
        >
          <Route index element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="*" element={<LandingPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
      <Route path="/rsvp/:token" element={<PublicRsvpPage />} />
      <Route path="/rsvp/:token/seat" element={<SeatFinderPage />} />
      <Route path="/i/:token" element={<InviteShareRedirect />} />
      <Route path="/upload/:token" element={<PublicUploadPage />} />
      {/* The address older emails carry — the same page. */}
      <Route path="/vendor-upload/:token" element={<PublicUploadPage />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />

        {/* Client Portal */}
        <Route
          path="client"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="client/events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientEventDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Vendor Portal */}
        <Route
          path="vendor"
          element={
            <ProtectedRoute minimumRole={SystemRole.VENDOR}>
              <VendorDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Invitations (PLANNER+) */}
        <Route
          path="invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/checkin"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CheckInPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="users"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="calendar"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <EventsPage />
            </ProtectedRoute>
          }
        />
        {/* Event workspace — pinned header + tabs; each tab at its own address.
            The layout lets clients through to the pages they already had
            (timeline, task lists, guests…) and gives them no producer header. */}
        <Route
          path="events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <EventWorkspacePage />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <EventOverviewTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="guests"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <GuestListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="invites"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <InvitesTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="invites/:campaignId"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <InviteCampaignTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="vendors"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <VendorsExpensesTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="expenses"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <Navigate to="../vendors" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="money"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <MoneyTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="timeline"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <TimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="timeline/:timelineId"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <TimelineEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="floor-plan"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <FloorPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="seating"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <SeatingTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="checklist"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <TaskListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="kibuddim"
            element={
              <ProtectedRoute minimumRole={SystemRole.CLIENT}>
                <KibuddimPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="questionnaires"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <QuestionnairesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="questionnaires/:questionnaireId"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <QuestionnaireDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <NotificationsTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="files"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <FilesPhotosTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="conversations"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <ConversationsTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="notes"
            element={
              <ProtectedRoute minimumRole={SystemRole.PLANNER}>
                <NotesTab />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="venues"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="venues/:venueId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenueDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors/:vendorId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="commissions"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CommissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="timelines"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="timelines/:timelineId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelineEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assistant"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <AssistantPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        {/* The page's old address, kept so bookmarks and links still land. */}
        <Route
          path="checklists"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="kibuddim"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <KibuddimPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="guests"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <GuestListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="floor-plans"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <FloorPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="photos"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <PhotosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/questionnaire/:questionnaireId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnaireDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/timeline/:templateId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelineTemplateEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        {/*
          No minimum role: everybody who signs in has notifications, and what
          reaches them is decided per recipient rather than per role.
        */}
        <Route path="notifications" element={<NotificationsPage />} />
        {/*
          PLANNER+, because a client's own conversations reach them through the
          portal and this lists correspondence across every record they are on.
          Widening it is H5's decision, not this route's.
        */}
        <Route
          path="communications"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CommunicationHubPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<CommunicationsPage />} />
          <Route path="inquiries" element={<InquiriesPage />} />
        </Route>
        <Route
          path="files"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading files…</div>}>
                <FilesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="import"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <DataImportPage />
            </ProtectedRoute>
          }
        />
        {/*
          System: the administrators' tools as one page, each tab at the
          address it had as a page of its own.
        */}
        <Route
          path="admin"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <SystemPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/mail-log" replace />} />
          <Route path="mail-log" element={<MailLogPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="log-alerts" element={<LogAlertsPage />} />
          <Route path="backups" element={<BackupsPage />} />
          <Route path="sql-client" element={<AdminSqlClientPage />} />
        </Route>
        {/* The old addresses, for bookmarks and mail already sent. */}
        <Route path="admin/email-logs" element={<Navigate to="/admin/mail-log" replace />} />
        <Route
          path="admin/inquiries"
          element={<Navigate to="/communications/inquiries" replace />}
        />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
    </Routes>
  );
}
